import { Link } from "react-router-dom";
import type { NewsItem } from "@/types/content";

interface NewsCardProps {
  item: NewsItem;
  variant?: "grid" | "list";
  index?: number;
}

const typeColor: Record<string, string> = {
  新闻: "bg-primary-100 text-primary-700",
  采访: "bg-accent-100 text-accent-700",
  活动: "bg-secondary-100 text-secondary-900",
};

export default function NewsCard({ item, variant = "grid", index = 0 }: NewsCardProps) {
  if (variant === "list") {
    return (
      <Link
        to={`/news/${item.id}`}
        className="group flex flex-col gap-3 rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-300 sm:flex-row sm:items-stretch animate-fade-up cursor-pointer"
        style={{ animationDelay: `${Math.min(index, 8) * 50}ms` }}
      >
        <div className="w-full shrink-0 overflow-hidden rounded-md bg-secondary-100 sm:w-40">
          <div className="aspect-[8/5] w-full">
            <img
              src={item.cover}
              alt={`${item.title} 配图`}
              title={`天海翼 ${item.title}`}
              className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
            />
          </div>
        </div>
        <div className="flex min-w-0 flex-1 flex-col justify-between">
          <div>
            <div className="mb-2 flex flex-wrap items-center gap-2 text-xs text-foreground-500">
              <span className={`rounded-md px-2 py-0.5 text-[11px] ${typeColor[item.type] || typeColor["新闻"]}`}>
                {item.type}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line" />
                {item.date}
              </span>
            </div>
            <h3 className="font-heading text-base font-semibold text-foreground-950 line-clamp-2 transition-colors group-hover:text-primary-700">
              {item.title}
            </h3>
            <p className="mt-1.5 text-sm leading-relaxed text-foreground-600 clamp-2">{item.excerpt}</p>
          </div>
          <span className="mt-2 hidden text-xs text-primary-700 sm:block">
            阅读全文
            <i className="ri-arrow-right-line ml-1 align-middle" />
          </span>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/news/${item.id}`}
      className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300 animate-fade-up cursor-pointer"
      style={{ animationDelay: `${Math.min(index, 8) * 60}ms` }}
    >
      <div className="w-full overflow-hidden bg-secondary-100">
        <div className="aspect-[8/5] w-full">
          <img
            src={item.cover}
            alt={`${item.title} 配图`}
            title={`天海翼 ${item.title}`}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        </div>
      </div>
      <div className="flex flex-1 flex-col p-4">
        <div className="mb-2 flex flex-wrap items-center gap-2 text-xs text-foreground-500">
          <span className={`rounded-md px-2 py-0.5 text-[11px] ${typeColor[item.type] || typeColor["新闻"]}`}>
            {item.type}
          </span>
          <span>{item.date}</span>
        </div>
        <h3 className="font-heading text-base font-semibold text-foreground-950 line-clamp-2 transition-colors group-hover:text-primary-700">
          {item.title}
        </h3>
        <p className="mt-2 text-sm leading-relaxed text-foreground-600 clamp-2">{item.excerpt}</p>
      </div>
    </Link>
  );
}